// src/sim/terrain.ts -- pure, no DOM
import { mulberry32, type Rng } from './rng';

export interface TerrainBuffer {
  data: Uint8ClampedArray; // RGBA, length w*h*4
  width: number;
  height: number;
}

export class Terrain {
  readonly width: number;
  readonly height: number;
  readonly buffer: TerrainBuffer;
  // dirty rect tracking for putImageData (PRF-03)
  dirtyMinX = Infinity;
  dirtyMinY = Infinity;
  dirtyMaxX = -Infinity;
  dirtyMaxY = -Infinity;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.buffer = {
      width,
      height,
      data: new Uint8ClampedArray(width * height * 4),
    };
  }

  /** Procedural generation: layered cosine noise heightmap. */
  generate(seed: number): void {
    const rng = mulberry32(seed);
    const heights = generateHeightmap(this.width, rng);
    // Fill: pixels below heightmap = dirt (opaque), above = sky (transparent)
    const d = this.buffer.data;
    for (let x = 0; x < this.width; x++) {
      const groundY = heights[x];
      for (let y = 0; y < this.height; y++) {
        const i = (y * this.width + x) * 4;
        if (y >= groundY) {
          // Simple shading: top crust lighter, deep darker
          const depth = Math.min((y - groundY) / 30, 1);
          d[i] = 140 - depth * 60; // R
          d[i + 1] = 90 - depth * 40; // G
          d[i + 2] = 40 - depth * 20; // B
          d[i + 3] = 255; // A: solid
        } else {
          d[i + 3] = 0; // sky: transparent
        }
      }
    }
    this.markDirty(0, 0, this.width, this.height);
  }

  /** True if pixel (x,y) is solid dirt. Used by collision. */
  isSolid(x: number, y: number): boolean {
    const ix = x | 0,
      iy = y | 0;
    if (ix < 0 || iy < 0 || ix >= this.width || iy >= this.height) return false;
    return this.buffer.data[(iy * this.width + ix) * 4 + 3] !== 0;
  }

  /** Carve a circular crater. Sets alpha=0 for all dirt pixels within radius. */
  carve(cx: number, cy: number, radius: number): void {
    const minX = Math.max(0, Math.floor(cx - radius));
    const maxX = Math.min(this.width - 1, Math.ceil(cx + radius));
    const minY = Math.max(0, Math.floor(cy - radius));
    const maxY = Math.min(this.height - 1, Math.ceil(cy + radius));
    const r2 = radius * radius;
    const d = this.buffer.data;
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        const dx = x - cx,
          dy = y - cy;
        if (dx * dx + dy * dy <= r2) {
          d[(y * this.width + x) * 4 + 3] = 0;
        }
      }
    }
    this.markDirty(minX, minY, maxX + 1, maxY + 1);
  }

  /** Return first solid y coordinate scanning from top. Returns height if column is all sky. */
  surfaceY(x: number): number {
    const ix = Math.max(0, Math.min(this.width - 1, x | 0));
    const d = this.buffer.data;
    for (let y = 0; y < this.height; y++) {
      if (d[(y * this.width + ix) * 4 + 3] !== 0) return y;
    }
    return this.height;
  }

  /** Add dirt pixels in a circle. Only fills sky (alpha===0) pixels. */
  addDirt(cx: number, cy: number, radius: number): void {
    const minX = Math.max(0, Math.floor(cx - radius));
    const maxX = Math.min(this.width - 1, Math.ceil(cx + radius));
    const minY = Math.max(0, Math.floor(cy - radius));
    const maxY = Math.min(this.height - 1, Math.ceil(cy + radius));
    const r2 = radius * radius;
    const d = this.buffer.data;
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        const dx = x - cx, dy = y - cy;
        if (dx * dx + dy * dy <= r2) {
          const idx = (y * this.width + x) * 4;
          if (d[idx + 3] === 0) {
            // Sky pixel -> fill with brown dirt
            d[idx] = 120;     // R
            d[idx + 1] = 80;  // G
            d[idx + 2] = 40;  // B
            d[idx + 3] = 255; // A
          }
        }
      }
    }
    this.markDirty(minX, minY, maxX + 1, maxY + 1);
  }

  /** Returns and clears the current dirty rect. Used by render layer. */
  takeDirtyRect(): { x: number; y: number; w: number; h: number } | null {
    if (this.dirtyMaxX < this.dirtyMinX) return null;
    const r = {
      x: this.dirtyMinX,
      y: this.dirtyMinY,
      w: this.dirtyMaxX - this.dirtyMinX,
      h: this.dirtyMaxY - this.dirtyMinY,
    };
    this.dirtyMinX = Infinity;
    this.dirtyMinY = Infinity;
    this.dirtyMaxX = -Infinity;
    this.dirtyMaxY = -Infinity;
    return r;
  }

  /** Marks the entire buffer as dirty. Used by collapse which can shift pixels anywhere. */
  markDirtyFull(): void {
    this.markDirty(0, 0, this.width, this.height);
  }

  private markDirty(x0: number, y0: number, x1: number, y1: number) {
    if (x0 < this.dirtyMinX) this.dirtyMinX = x0;
    if (y0 < this.dirtyMinY) this.dirtyMinY = y0;
    if (x1 > this.dirtyMaxX) this.dirtyMaxX = x1;
    if (y1 > this.dirtyMaxY) this.dirtyMaxY = y1;
  }
}

/** 1D heightmap via layered cosine noise -- produces gentle hills. */
function generateHeightmap(width: number, rng: Rng): Int32Array {
  const heights = new Int32Array(width);
  const baseY = 160;
  const amp = 50;
  const seeds = [rng() * 1000, rng() * 1000, rng() * 1000];
  for (let x = 0; x < width; x++) {
    const n =
      Math.cos((x + seeds[0]) * 0.012) * 0.6 +
      Math.cos((x + seeds[1]) * 0.031) * 0.3 +
      Math.cos((x + seeds[2]) * 0.07) * 0.1;
    heights[x] = Math.round(baseY + n * amp);
  }
  return heights;
}
